// 의견 보내기 대화상자 — 본문(2000자까지)과 지금 보는 화면 경로를 보낸다 · 조회만 조직도 보낼 수 있다(api 예외).

import { useState, useTransition } from "react";
import { toast } from "sonner";

import { FEEDBACK_BODY_MAX } from "@buildpy/core/modules/feedback/types";
import { Button } from "@buildpy/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@buildpy/ui/dialog";
import { Textarea } from "@buildpy/ui/textarea";
import { sendFeedback } from "@/api/actions/feedback";

// 지금 화면 경로 — 쿼리·해시는 빼고 200자까지(표 제약과 같다).
export const feedbackPageOf = (pathname: string | null | undefined): string | null => {
  const path = (pathname ?? "").trim();
  return path === "" ? null : path.slice(0, 200);
};

const currentPath = (): string | null => {
  try {
    return feedbackPageOf(window.location.pathname);
  } catch {
    return null;
  }
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const FeedbackDialog = ({ open, onOpenChange }: Props) => {
  const [body, setBody] = useState("");
  const [pending, startTransition] = useTransition();
  const empty = body.trim() === "";

  const submit = () => {
    if (empty || pending) return;
    startTransition(async () => {
      try {
        const result = await sendFeedback({ body, page: currentPath() });
        if (!result.ok) {
          toast.error(result.message);
          return;
        }
        toast.success(result.message);
        setBody("");
        onOpenChange(false);
      } catch {
        toast.error("의견을 보내지 못했습니다 — 잠시 뒤 다시 시도해 주세요");
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="feedback-dialog">
        <DialogHeader>
          <DialogTitle>의견 보내기</DialogTitle>
          <DialogDescription>불편한 점이나 바라는 기능을 적어 주세요. buildpy 운영자가 읽고 반영합니다.</DialogDescription>
        </DialogHeader>
        <Textarea
          aria-label="의견"
          data-testid="feedback-body"
          maxLength={FEEDBACK_BODY_MAX}
          placeholder="예) 견적서에서 공정 순서를 끌어서 바꾸고 싶어요"
          rows={6}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <p className="text-right text-xs tabular-nums text-muted-foreground">
          {body.length} / {FEEDBACK_BODY_MAX}
        </p>
        <DialogFooter>
          <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)}>
            취소
          </Button>
          <Button data-testid="feedback-send" disabled={empty || pending} size="sm" onClick={submit}>
            {pending ? "보내는 중…" : "보내기"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackDialog;
